<script lang="ts">
  import { onMount } from 'svelte';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import { flip } from 'svelte/animate';
  import { Plus } from 'lucide-svelte';
  import { boardApi, noWorkApi } from '$lib/api/board';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import BoardCardSourceBadge from '$lib/components/ui/BoardCardSourceBadge.svelte';
  import BoardDueBadge from '$lib/components/ui/BoardDueBadge.svelte';
  import BoardSeenBadge from '$lib/components/ui/BoardSeenBadge.svelte';
  import { trackBoardView } from '$lib/utils/boardViewTracker';
  import BoardDateInput from '$lib/components/ui/BoardDateInput.svelte';
  import BoardUpdatesSheet from './BoardUpdatesSheet.svelte';
  import { selectsRequester } from '$lib/utils/boardHandoff';
  import BoardCheckInPanel from './BoardCheckInPanel.svelte';
  import { boardToday, boardReminders, boardCheckInDismissalKey } from '$lib/utils/board';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import NoWorkModal from './NoWorkModal.svelte';
  import { t } from '$lib/i18n';
  import type { MessageKey } from '$lib/i18n/en';
  import type { BoardCard, BoardColumn, BoardUpdateStatus } from '$lib/types';

  const BOARD_COL_KEY = 'board-selected-col';

  const COLUMNS: { id: BoardColumn; labelKey: MessageKey; dot: string }[] = [
    { id: 'todo', labelKey: 'board.column.todo', dot: '#94A2B0' },
    { id: 'doing', labelKey: 'board.column.doing', dot: '#226A73' },
    { id: 'blocked', labelKey: 'board.column.blocked', dot: '#C8412B' },
    { id: 'done', labelKey: 'board.column.done', dot: '#5B7A00' }
  ];

  function colLabel(id: BoardColumn): string {
    return t(COLUMNS.find((c) => c.id === id)!.labelKey);
  }

  const queryClient = useQueryClient();
  // Usage telemetry is intentionally non-blocking and has no UI consequence.
  boardApi.recordOpen().catch(() => {});
  const cardsQuery = createQuery(() => ({
    queryKey: ['board'],
    queryFn: () => boardApi.list()
  }));
  const linkedTeamQuery = createQuery(() => {
    const r = route.current;
    return {
      queryKey: ['board-team'],
      queryFn: () => boardApi.team(),
      enabled: Boolean(session.canViewTeamBoard && cardsQuery.data && r.tab === 'board' &&
        r.sub === 'card' && !cardsQuery.data.some((card) => card.id === r.cardId))
    };
  });
  const noWorkQuery = createQuery(() => ({
    queryKey: ['no-work-days'],
    queryFn: () => noWorkApi.list()
  }));

  function refreshCards() {
    queryClient.invalidateQueries({ queryKey: ['board'] });
    queryClient.invalidateQueries({ queryKey: ['board-team'] });
    queryClient.invalidateQueries({ queryKey: ['board-team-activity'] });
  }
  function refreshNoWork() {
    queryClient.invalidateQueries({ queryKey: ['no-work-days'] });
  }

  let columnItems = $state<Record<BoardColumn, BoardCard[]>>({ todo: [], doing: [], blocked: [], done: [] });
  let lastSyncedFrom: BoardCard[] | undefined;
  $effect(() => {
    const data = cardsQuery.data;
    if (!data || data === lastSyncedFrom) return;
    lastSyncedFrom = data;
    columnItems = {
      todo: data.filter((c) => c.col === 'todo'),
      doing: data.filter((c) => c.col === 'doing'),
      blocked: data.filter((c) => c.col === 'blocked'),
      done: data.filter((c) => c.col === 'done')
    };
  });

  function cardsForCol(col: BoardColumn): BoardCard[] {
    return columnItems[col];
  }

  let selectedCol = $state<BoardColumn>('todo');
  $effect(() => {
    const stored = sessionStorage.getItem(BOARD_COL_KEY);
    if (stored && COLUMNS.some((c) => c.id === stored)) {
      selectedCol = stored as BoardColumn;
      sessionStorage.removeItem(BOARD_COL_KEY);
    }
  });

  let editing = $state<BoardCard | null>(null);
  let createCol = $state<BoardColumn>('todo');
  let showForm = $state(false);
  let form = $state({ title: '', description: '', due_date: '' });
  let createStep = $state(1);
  let updateCard = $state<BoardCard | null>(null);
  let updateStatus = $state<BoardUpdateStatus>('on_track');
  let updateText = $state('');
  let createStatus = $state<BoardUpdateStatus | ''>('');
  let createWaitingOn = $state('');
  let dismissedDay = $state('');
  let currentDay = $state(boardToday());
  onMount(() => {
    const timer = setInterval(() => { currentDay = boardToday(); }, 60_000);
    return () => clearInterval(timer);
  });
  const dismissalKey = $derived(boardCheckInDismissalKey(session.me?.id));
  $effect(() => {
    try { dismissedDay = localStorage.getItem(dismissalKey) ?? ''; } catch { /* Storage can be disabled. */ }
  });
  const panelOpen = $derived(dismissedDay !== currentDay);
  function dismissPanel() {
    dismissedDay = boardToday();
    try { localStorage.setItem(dismissalKey, dismissedDay); } catch { /* Keep in-memory dismissal. */ }
    if (route.current.tab === 'board' && route.current.checkin) route.replace({ tab: 'board' });
  }
  function showPanel() {
    dismissedDay = '';
    try { localStorage.removeItem(dismissalKey); } catch { /* Keep in-memory state. */ }
  }
  $effect(() => {
    const r = route.current;
    if (r.tab === 'board' && (r.checkin || r.sub === 'cos')) showPanel();
  });
  let handledCardId: number | undefined;
  const reminders = $derived(boardReminders(cardsQuery.data ?? [], currentDay));
  $effect(() => {
    const r = route.current;
    const id = r.tab === 'board' && r.sub === 'card' ? r.cardId : undefined;
    if (!id) { handledCardId = undefined; updateCard = null; return; }
    if (id === handledCardId || !cardsQuery.data) return;
    handledCardId = id;
    const card = cardsQuery.data.find((c) => c.id === id) ?? linkedTeamQuery.data?.cards.find((c) => c.id === id);
    if (!card && session.canViewTeamBoard && !linkedTeamQuery.data && !linkedTeamQuery.isError) {
      handledCardId = undefined;
      return;
    }
    if (card) { selectedCol = card.col; updateStatus = card.col === 'blocked' ? 'blocked' : card.col === 'done' ? 'done' : 'on_track'; updateText = ''; updateCard = card; }
    else toast.error(t('board.updates.unavailable'));
  });
  function openUpdates(card: BoardCard, status?: BoardUpdateStatus) {
    handledCardId = card.id;
    updateStatus = status ?? (card.col === 'blocked' ? 'blocked' : card.col === 'done' ? 'done' : 'on_track');
    updateText = status ? t(`board.quick.text.${status}`) : '';
    updateCard = card;
    route.push({ tab: 'board', sub: 'card', cardId: card.id, update: true });
  }
  function closeUpdates() {
    updateCard = null;
    route.replace({ tab: 'board' });
  }
  let saving = $state(false);
  let error = $state('');

  const totalCards = $derived((cardsQuery.data ?? []).length);
  const isEmptyBoard = $derived(totalCards === 0);

  function openCreate(col: BoardColumn) {
    editing = null;
    createCol = col;
    form = { title: '', description: '', due_date: '' };
    createStep = 1;
    createStatus = '';
    createWaitingOn = '';
    error = '';
    showForm = true;
  }
  function openEdit(card: BoardCard) {
    editing = card;
    form = { title: card.title, description: card.description, due_date: card.due_date?.slice(0, 10) ?? '' };
    error = '';
    showForm = true;
  }

  async function save() {
    if (!form.title.trim()) {
      error = t('board.error.titleRequired');
      return;
    }
    saving = true;
    error = '';
    try {
      const body = { ...form, title: form.title.trim(), due_date: form.due_date };
      if (editing) await boardApi.update(editing.id, body);
      else {
        const created = await boardApi.create({ ...body, col: createCol === 'blocked' ? 'todo' : createCol });
        selectedCol = created.col;
        if (createCol === 'blocked') openBlock(created);
        else if (createStatus || createWaitingOn.trim()) {
          try {
            await boardApi.addUpdate(created.id, { text: (form.description.trim() || form.title.trim()).slice(0, 1000), status: createStatus || 'waiting', waiting_on: createWaitingOn.trim(), next_followup: null });
          } catch (err) { toast.error(getErrorMessage(err)); }
        }
      }
      refreshCards();
      showForm = false;
      toast.success(editing ? t('board.toast.updated') : t('board.toast.created'));
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function remove(card: BoardCard) {
    if (!confirm(t('board.confirmDelete', { title: card.title }))) return;
    try {
      await boardApi.remove(card.id);
      refreshCards();
      toast.success(t('board.toast.deleted'));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function moveCard(card: BoardCard, col: BoardColumn) {
    if (col === card.col) return;
    if (col === 'blocked') { openBlock(card); return; }
    try {
      await boardApi.update(card.id, { col });
      refreshCards();
      toast.success(t('board.toast.moved', { col: colLabel(col) }));
    } catch (err) {
      toast.error(getErrorMessage(err));
      refreshCards();
    }
  }

  function handleConsider(col: BoardColumn, e: CustomEvent<DndEvent<BoardCard>>) {
    columnItems[col] = e.detail.items;
  }

  function handleFinalize(col: BoardColumn, e: CustomEvent<DndEvent<BoardCard>>) {
    columnItems[col] = e.detail.items;
    const moved = e.detail.items.find((c) => c.col !== col);
    if (moved) moveCard(moved, col);
  }

  function cardMenu(card: BoardCard) {
    const items: { label: string; onClick: () => void; danger?: boolean }[] = [
      { label: t('board.updates.add'), onClick: () => openUpdates(card) },
      { label: t('common.edit'), onClick: () => openEdit(card) }
    ];
    for (const c of COLUMNS) {
      if (c.id !== card.col) items.push({ label: t('board.moveTo', { col: t(c.labelKey) }), onClick: () => moveCard(card, c.id) });
    }
    items.push({ label: t('board.deleteCard'), onClick: () => remove(card), danger: true });
    return items;
  }

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function todayStr(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  }
  const loggedToday = $derived((noWorkQuery.data ?? []).some((d) => d.day.slice(0, 10) === todayStr()));

  let showNoWork = $state(false);
  let blockingCard = $state<BoardCard | null>(null);
  let blockReason = $state('');
  let blockMode = $state<'helpers' | 'external'>('helpers');
  let externalName = $state('');
  type HelperRow = { user_id: number | null; name: string; title: string; query: string; matches: { id: number; name: string }[]; lookup: number; timer: ReturnType<typeof setTimeout> | undefined };
  const newHelper = (): HelperRow => ({ user_id: null, name: '', title: '', query: '', matches: [], lookup: 0, timer: undefined });
  let helpers = $state<HelperRow[]>([]);
  let blockSaving = $state(false);
  let blockError = $state('');
  function needsBlockingInfo(card: BoardCard): boolean {
    return card.col === 'blocked' && !(card.blockers?.length) && !card.blocked_external_name;
  }
  function openBlock(card: BoardCard) {
    blockingCard = card;
    blockReason = card.col === 'blocked' ? (card.blocked_reason ?? '') : '';
    externalName = card.col === 'blocked' ? (card.blocked_external_name ?? '') : '';
    blockMode = card.blocked_external_name ? 'external' : 'helpers';
    helpers = [newHelper()];
    blockError = '';
  }
  function closeBlock(reverted: boolean) {
    for (const row of helpers) if (row.timer) clearTimeout(row.timer);
    blockingCard = null;
    if (reverted) refreshCards();
  }
  function lookupHelper(row: HelperRow) {
    row.user_id = null;
    row.name = '';
    row.matches = [];
    const lookup = ++row.lookup;
    if (row.timer) clearTimeout(row.timer);
    row.timer = setTimeout(async () => {
      const query = row.query.trim();
      const matches = query ? await boardApi.mentions(query).catch(() => []) : [];
      if (row.lookup === lookup && row.query.trim() === query) row.matches = matches;
    }, 180);
  }
  function selectHelper(row: HelperRow, person: { id: number; name: string }) {
    row.user_id = person.id;
    row.name = person.name;
    row.query = person.name;
    row.matches = [];
  }
  const blockRequesterSelected = $derived(Boolean(blockingCard && blockMode === 'helpers' && selectsRequester(blockingCard, helpers)));
  const blockValid = $derived(!blockRequesterSelected && (
    blockMode === 'external'
      ? Boolean(blockReason.trim() && externalName.trim())
      : Boolean(blockReason.trim() && helpers.length > 0 && helpers.every((x) => x.user_id && x.title.trim()))
  ));
  async function submitBlock() {
    if (!blockingCard || !blockValid || blockSaving) return;
    blockSaving = true;
    blockError = '';
    try {
      const body =
        blockMode === 'external'
          ? { reason: blockReason.trim(), helpers: [], external: { name: externalName.trim() } }
          : { reason: blockReason.trim(), helpers: helpers.map((x) => ({ user_id: x.user_id!, title: x.title.trim() })) };
      await boardApi.block(blockingCard.id, body);
      closeBlock(false);
      refreshCards();
      toast.success(t('board.toast.blocked'));
    } catch (e) {
      blockError = getErrorMessage(e);
    } finally {
      blockSaving = false;
    }
  }
  const ownCards = $derived((cardsQuery.data ?? []).filter((c) => c.col === 'todo' || c.col === 'blocked'));
</script>

<div class="tym-page">
  {#if panelOpen}
    <BoardCheckInPanel cards={reminders} loading={cardsQuery.isPending} failed={cardsQuery.isError} onCreate={() => openCreate('todo')} onDismiss={dismissPanel} onUpdate={openUpdates} />
  {/if}
  <Toolbar>
    {#snippet actions()}
      {#if !panelOpen}<button type="button" class="btn-secondary min-h-[44px]" onclick={showPanel}>{t('board.reminders.title')}</button>{/if}
      <button type="button" class="btn-primary min-h-[44px]" onclick={() => openCreate('todo')}>{t('board.addCard')}</button>
      <button
        type="button"
        class="btn-secondary"
        disabled={loggedToday}
        onclick={() => (showNoWork = true)}
        title={loggedToday ? t('board.loggedTodayTitle') : undefined}
      >
        {loggedToday ? t('board.loggedToday') : t('board.noWorkToday')}
      </button>
    {/snippet}
  </Toolbar>

  <FilterChipRow
    class="tym-board-chiprow mb-3 md:hidden"
    chips={COLUMNS.map((c) => ({ id: c.id, label: t(c.labelKey), count: cardsForCol(c.id).length }))}
    value={selectedCol}
    onSelect={(id) => (selectedCol = id as BoardColumn)}
    ariaLabel={t('board.columnAria')}
    sticky
  />

  {#if cardsQuery.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if cardsQuery.isError}
    <div class="tym-error-state flex flex-wrap items-center justify-between gap-3">
      <span>{t('board.error')}</span>
      <button type="button" class="btn-secondary !min-h-[36px] !px-3 !text-[12.5px]" onclick={refreshCards}>
        {t('common.retry')}
      </button>
    </div>
  {:else if cardsQuery.data}
    {#if isEmptyBoard}
      <div class="tym-empty-state mb-4">
        <span class="tym-empty-title">{t('board.emptyBoard')}</span>
        <button type="button" class="btn-primary mt-3 !min-h-[44px]" onclick={() => openCreate('todo')}>
          {t('board.addCard')}
        </button>
      </div>
    {/if}

    <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
      {#each COLUMNS as c (c.id)}
        <div class="{selectedCol === c.id ? '' : 'hidden'} md:block">
          <section class="tym-board-column">
            <div class="tym-board-column-header">
              <span class="h-[7px] w-[7px] shrink-0 rounded-full" style="background:{c.dot}"></span>
              <p class="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-navy">{t(c.labelKey)}</p>
              <span class="tym-board-column-count" aria-hidden="true">{cardsForCol(c.id).length}</span>
              <span class="flex-1"></span>
              <button
                type="button"
                class="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-dark-gray transition-colors hover:bg-surface hover:text-navy"
                onclick={() => openCreate(c.id)}
                aria-label={t('board.addCardTo', { col: t(c.labelKey) })}
              >
                <Plus class="h-3.5 w-3.5" />
              </button>
            </div>
            <div class="relative">
              <div
                class="flex min-h-[72px] flex-col gap-1.5"
                use:dndzone={{ items: columnItems[c.id], flipDurationMs: 150, delayTouchStart: true }}
                onconsider={(e) => handleConsider(c.id, e)}
                onfinalize={(e) => handleFinalize(c.id, e)}
              >
                {#each columnItems[c.id] as card (card.id)}
                  <article
                    use:trackBoardView={{ card, userId: session.me?.id }}
                    animate:flip={{ duration: 150 }}
                    class="tym-board-card cursor-grab rounded-[10px] border border-toyama-cloudy-gray bg-surface py-2.5 pl-3 pr-2"
                    style="--tym-board-accent:{c.dot}"
                  >
                    <div class="flex items-start gap-1.5">
                      <div class="min-w-0 flex-1">
                        <p class="text-[13px] font-semibold leading-[1.35] text-navy" style="text-wrap:pretty">{card.title}</p>
                        {#if card.description}
                          <p class="mt-1 text-[11.5px] leading-[1.4] text-dark-gray" style="text-wrap:pretty">
                            {card.description}
                          </p>
                        {/if}
                        <BoardCardSourceBadge {card} />
                        <BoardDueBadge {card} />
                        <BoardSeenBadge {card} />
                        <div class="mt-2 flex flex-wrap gap-1">
                          <button type="button" class="min-h-[44px] rounded-md px-2 text-xs font-semibold text-toyama-teal hover:bg-surface-2" onclick={() => openUpdates(card)}>{t('board.updates.add')}</button>
                          {#if card.col !== 'done'}<button type="button" class="min-h-[44px] rounded-md px-2 text-xs font-semibold text-toyama-teal hover:bg-surface-2" onclick={() => openUpdates(card, 'on_track')}>{t('board.quick.start')}</button>{/if}
                          {#if card.col !== 'done'}<button type="button" class="min-h-[44px] rounded-md px-2 text-xs font-semibold text-toyama-teal hover:bg-surface-2" onclick={() => openUpdates(card, 'done')}>{t('board.quick.done')}</button>{/if}
                        </div>
                        {#if card.group_total > 1}<p class="mt-1 text-[11px] text-dark-gray">{t('board.sharedProgress', { completed: card.group_completed, total: card.group_total })}</p>{/if}
                        {#if card.col === 'blocked' && card.blocked_reason}<p class="mt-1 text-[11px] text-red-700">{t('board.blockedReason', { reason: card.blocked_reason })}</p>{/if}
                        {#if card.col === 'blocked' && card.blocked_external_name}<p class="mt-1 text-[11px] text-dark-gray">{t('board.blockedExternal', { name: card.blocked_external_name })}</p>{/if}
                        {#if card.blockers?.length}<p class="mt-1 text-[11px] text-dark-gray">{t('board.helpers', { helpers: card.blockers.map((x) => `${x.user_name} (${x.col})`).join(', ') })}</p>{/if}
                        {#if card.blocking}<p class="mt-1 text-[11px] text-dark-gray">{t('board.blocking', { user: card.blocking.user_name, title: card.blocking.title })}</p>{/if}
                        {#if needsBlockingInfo(card)}<button type="button" class="mt-1.5 min-h-[44px] rounded-md bg-red-50 px-2 text-left text-[11.5px] font-semibold text-red-700" onclick={() => openBlock(card)}>{t('board.block.missingInfoCta')}</button>{/if}
                        {#if card.col === 'done' && card.done_at}
                          <span
                            class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-surface-2 px-1.5 py-px text-[10.5px] tabular-nums text-dark-gray"
                          >
                            {t('board.doneAt', { date: fmtDate(card.done_at) })}
                          </span>
                        {/if}
                      </div>
                      <RowActions items={cardMenu(card)} />
                    </div>
                  </article>
                {/each}
              </div>
              {#if columnItems[c.id].length === 0}
                <button
                  type="button"
                  class="absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded-[10px] border border-dashed border-toyama-cloudy-gray text-dark-gray transition-colors hover:border-toyama-teal hover:text-toyama-teal"
                  onclick={() => openCreate(c.id)}
                >
                  <Plus class="h-3.5 w-3.5" />
                  <span class="text-[11.5px] font-medium">{t('board.addCard')}</span>
                </button>
              {/if}
            </div>
          </section>
        </div>
      {/each}
    </div>
  {/if}

  {#if showForm}
    <Modal title={editing ? t('board.editCard') : t('board.newCard', { col: colLabel(createCol) })} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        {#if !editing}<p class="text-sm text-dark-gray sm:hidden" aria-live="polite">{t('board.create.step', { n: createStep })}</p>{/if}
        <div class={!editing && createStep !== 1 ? 'hidden sm:block' : ''}>
          <div class="label">{t('board.title')}</div>
          <input type="text" class="input" aria-label={t('board.title')} bind:value={form.title} />
          {#if !editing}<p class="mt-2 text-xs text-dark-gray">{t('board.create.guidance')}</p>{/if}
        </div>
        <div class={!editing && createStep !== 2 ? 'hidden sm:block' : ''}>
          <div class="label">{t('board.description')}</div>
          <textarea class="input" rows="2" aria-label={t('board.description')} bind:value={form.description}></textarea>
          {#if !editing && createCol !== 'blocked'}
            <label class="label mt-3">{t('board.updates.waitingOn')}<input class="input mt-1 min-h-[44px]" maxlength="80" bind:value={createWaitingOn} /></label>
            <label class="label mt-3">{t('board.updates.status')}
              <select class="input mt-1 min-h-[44px]" bind:value={createStatus}>
                <option value="">{t('board.create.keepStatus')}</option>
                <option value="on_track">{t('board.status.on_track')}</option>
                <option value="waiting">{t('board.status.waiting')}</option>
                <option value="blocked">{t('board.status.blocked')}</option>
                <option value="done">{t('board.status.done')}</option>
              </select>
            </label>
          {/if}
        </div>
        <div class={!editing && createStep !== 3 ? 'hidden sm:block' : ''}>
          <BoardDateInput bind:value={form.due_date} />
          {#if !editing}<p class="mt-3 break-words text-sm font-semibold text-navy sm:hidden">{form.title}</p>{/if}
        </div>
        {#if error}<div class="tym-error-state">{error}</div>{/if}
      </div>
      {#snippet footer()}
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>{t('common.cancel')}</button>
          {#if !editing}
            {#if createStep > 1}<button type="button" class="btn-secondary sm:hidden" onclick={() => createStep--}>{t('board.create.back')}</button>{/if}
            {#if createStep < 3}<button type="button" class="btn-primary sm:hidden" disabled={!form.title.trim()} onclick={() => createStep++}>{t('board.create.next')}</button>{/if}
          {/if}
          <button type="button" class="btn-primary {!editing && createStep < 3 ? 'hidden sm:inline-flex' : ''}" disabled={saving} onclick={save}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      {/snippet}
    </Modal>
  {/if}

  {#if updateCard}
    {#key updateCard.id}<BoardUpdatesSheet card={updateCard} initialStatus={updateStatus} initialText={updateText} onClose={closeUpdates} onSaved={refreshCards} />{/key}
  {/if}

  {#if showNoWork}
    <NoWorkModal
      {ownCards}
      recentDays={noWorkQuery.data ?? []}
      onClose={() => (showNoWork = false)}
      onSuccess={() => {
        refreshNoWork();
        toast.success(t('board.toast.logged'));
      }}
    />
  {/if}
  {#if blockingCard}
    <Modal title={t('board.block.title')} onClose={() => closeBlock(true)}>
      <div class="space-y-4">
        <p class="text-sm text-dark-gray">{t('board.block.intro', { title: blockingCard.title })}</p>
        {#if blockingCard.blocking}<p class="text-sm font-semibold text-navy">{t('board.return.helping', { owner: blockingCard.blocking.user_name, title: blockingCard.blocking.title })}</p>{/if}
        <label class="label">{t('board.block.reason')}<input class="input mt-1 min-h-[44px]" bind:value={blockReason} /></label>
        <div class="flex gap-2" role="group" aria-label={t('board.block.modeLabel')}>
          <button type="button" class="btn-secondary min-h-[44px] flex-1 {blockMode === 'helpers' ? '!border-toyama-teal !text-navy' : ''}" aria-pressed={blockMode === 'helpers'} onclick={() => (blockMode = 'helpers')}>{t('board.block.modeHelpers')}</button>
          <button type="button" class="btn-secondary min-h-[44px] flex-1 {blockMode === 'external' ? '!border-toyama-teal !text-navy' : ''}" aria-pressed={blockMode === 'external'} onclick={() => (blockMode = 'external')}>{t('board.block.modeExternal')}</button>
        </div>
        {#if blockMode === 'external'}
          <label class="label">{t('board.block.externalName')}<input class="input mt-1 min-h-[44px]" autocomplete="off" bind:value={externalName} placeholder={t('board.block.externalNamePlaceholder')} /></label>
          <p class="text-xs leading-5 text-dark-gray">{t('board.block.externalGuidance')}</p>
        {:else}
        <p class="text-xs leading-5 text-dark-gray">{t('board.block.firstNameGuidance')}</p>
        {#each helpers as helper, i}
          <fieldset class="rounded-[10px] border border-toyama-cloudy-gray p-3">
            <legend class="px-1 text-xs font-semibold text-navy">{t('board.block.helperNumber', { n: i + 1 })}</legend>
            <label class="label">{t('board.block.helperFirstName')}<input class="input mt-1 min-h-[44px]" autocomplete="off" bind:value={helper.query} oninput={() => lookupHelper(helper)} /></label>
            {#if helper.name}<p class="mt-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-navy">{t('board.block.selectedHelper', { name: helper.name })}</p>{/if}
            {#if helper.matches.length}<div class="mt-2 flex flex-wrap gap-2" aria-label={t('board.block.matches')}>{#each helper.matches as person (person.id)}<button type="button" class="min-h-[44px] rounded-md bg-surface-2 px-3 text-sm text-navy hover:bg-toyama-cloudy-gray" onclick={() => selectHelper(helper, person)}>{person.name}</button>{/each}</div>{/if}
            <label class="label mt-3">{t('board.block.helperTask')}<input class="input mt-1 min-h-[44px]" bind:value={helper.title} /></label>
            {#if helpers.length > 1}<button type="button" class="mt-3 min-h-[44px] px-2 text-sm font-semibold text-red-700" onclick={() => helpers = helpers.filter((_, n) => n !== i)}>{t('board.block.removeHelper')}</button>{/if}
          </fieldset>
        {/each}
        {/if}
        {#if blockRequesterSelected}
          <p class="text-sm text-red-700" role="alert">{t('board.return.parentSelected')}</p>
          <button type="button" class="btn-secondary min-h-[44px]" onclick={() => { const card = blockingCard!; const draft = blockReason; closeBlock(false); openUpdates(card); updateText = draft; }}>{t('board.return.action')}</button>
        {/if}
        {#if blockError}<div class="tym-error-state" role="alert">{blockError}</div>{/if}
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {#if blockMode === 'helpers'}
          <button type="button" class="btn-secondary min-h-[44px]" onclick={() => helpers = [...helpers, newHelper()]}>{t('board.block.addHelper')}</button>
          {:else}<span></span>{/if}
          <button type="button" class="btn-primary min-h-[44px]" disabled={blockSaving || !blockValid} onclick={submitBlock}>{blockSaving ? t('common.saving') : t('board.block.submit')}</button>
        </div>
      </div>
    </Modal>
  {/if}
</div>
